<script lang="ts">
    import type { TileImage } from '@shared/types/types';
    import ProductImage from '@tile/components/product-image/ProductImage.svelte';
    import type { ImageAspectRatio, ImageLoadingOptions } from '@tile/components/product-image/types.ts';
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import type { ExperimentGroups } from '@tilelist/domain/experiments/Experiments.ts';
    import ProductImageCarousel from '@tilelist/layouts/experiments/e3891/product-image-carousel/ProductImageCarousel.svelte';
    import ProductImageE3909 from '@tilelist/layouts/experiments/e3909/product-image/ProductImage.svelte';
    import ProductImageE3918 from '@tilelist/layouts/experiments/e3918/product-image/ProductImage.svelte';

    interface Props {
        images: TileImage[];
        fallbackImage: TileImage;
        variationId: string;
        detailPageLink: string;
        imageLoadingOptions: ImageLoadingOptions;
        onClick?: (event: Event) => void;
        aspectRatio?: ImageAspectRatio;
        brand?: string;
        title?: string;
        linkType?: 'image' | 'tile';
    }

    let {
        images,
        fallbackImage,
        variationId,
        detailPageLink,
        imageLoadingOptions,
        onClick,
        aspectRatio,
        brand,
        title,
        linkType = 'image',
    }: Props = $props();

    const wiring = injectWiring(['trackingService']);
    const onex = wiring.onex;

    const additionalImagesMapping: Record<ExperimentGroups['ADDITIONAL_IMAGES'], boolean> = {
        E3891A: false,
        E3891B: true,
    };

    const consistentImagesMapping: Record<ExperimentGroups['CONSISTENT_IMAGES'], boolean> = {
        E3909A: false,
        E3909B: true,
    };

    const showAdditionalImages = $derived(onex.matchGroup('ADDITIONAL_IMAGES', additionalImagesMapping));
    const cleanTileActive = $derived(onex.isExperimentGroup('CLEAN_TILE_V3', 'E3918B'));
    const shouldApplyE3909 = $derived(onex.matchGroup('CONSISTENT_IMAGES', consistentImagesMapping));
</script>

{#snippet carousel(carouselLinkType: 'image' | 'tile')}
    <ProductImageCarousel
        {images}
        {fallbackImage}
        {variationId}
        {detailPageLink}
        brand={brand || ''}
        title={title || ''}
        {imageLoadingOptions}
        {onClick}
        onImageChange={(index) => wiring.trackingService?.trackProductImageChange(index)}
        {aspectRatio}
        size="100"
        linkType={carouselLinkType}
    />
{/snippet}

{#if showAdditionalImages}
    {#if cleanTileActive}
        <div data-secondary-action>
            {@render carousel('tile')}
        </div>
    {:else}
        {@render carousel('image')}
    {/if}
{:else if shouldApplyE3909}
    <ProductImageE3909
        image={fallbackImage}
        {variationId}
        {imageLoadingOptions}
        {aspectRatio}
        {detailPageLink}
        {onClick}
        {linkType}
    />
{:else if cleanTileActive}
    <ProductImageE3918 image={fallbackImage} {variationId} {imageLoadingOptions} {aspectRatio} />
{:else}
    <ProductImage {variationId} image={fallbackImage} {detailPageLink} {onClick} {imageLoadingOptions} {aspectRatio} />
{/if}
